import {  useEffect, useMemo, useState } from "react";
import "./dashboard.css";
import Sidebar from "../../components/Sidenav/sidenav.components";
import ReportCard from "../../components/ReportCard/reportcard.components";
import { Calendar } from 'primereact/calendar';
import { FloatLabel } from 'primereact/floatlabel';
import DataTable from "../../components/Datatable/Datatable";
import { Project } from '../../utils/types';
import { jwtDecode } from "jwt-decode";
import { getDashboard } from "../../services/auth.api";
import {  Row, Col } from 'react-bootstrap';
import gridActive from "../../assets/images/grid-active.svg";
import gridInActive from "../../assets/images/grid-inactive.svg";
import listActive from "../../assets/images/list-active.svg";
import listInActive from "../../assets/images/list-inactive.svg";
import { Dropdown } from 'primereact/dropdown';
import { isTokenValid } from "../../utils/authUtils";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import backArrowOutline from "../../assets/images/back-arrow-outline.svg";
interface Column {
  key: keyof Project;
  label: string;
}

interface User {
  firstName: string;
  lastName: string;
  email: string;
}

type Context = 'grid' | 'list';

const projectTableColumns: Column[] = [
  { key: 'accountName', label: 'Account Name' },
  { key: 'certificateId', label: 'Certificate Type' },
  { key: 'projectName', label: 'Project Name' },
  { key: 'projectStatus', label: 'Project Status' },
  { key: 'reportDueDate', label: 'Report Due Date' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [dates, setDates] = useState<Date[] | null>(null);
  const [data, setData] = useState<Project[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [context, setContext] = useState<Context>('list');

  useEffect(() => {
    console.log("Dashboard mounted");
  
    if (!isTokenValid()) {
      console.log("Invalid token");
      navigate('/logout');
      return;
    }
  
    const accessToken = localStorage.getItem('access_token') as string;
    const decodedToken: User = jwtDecode(accessToken);
    setUser(decodedToken);
  
    (async () => {
      try {
        console.log("Fetching dashboard...");
        const response = await getDashboard();
        console.log("Dashboard response:", response);
        if (response.projects) setData(response.projects);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoadingTable(false);
      }
    })()
  }, []);
  

  const handleViewChange = (view: 'grid' | 'list') => {
    setContext(view);
  };

  const { reportsDueCount, upcomingReportsCount } = useMemo(() => {
    const dateAfter3Days = moment().add(3, 'days').toDate();
    const dateAfter46Days = moment().add(46, 'days').toDate();

    const getTaskStartDate = (project: Project) => project.inspire1__Inspire_Tasks__r?.records[0]?.inspire1__Finish_Date_Only__c ?? null;

    const dueReports = data.filter((project: Project) => 
      moment(getTaskStartDate(project)).isBetween(moment(), dateAfter3Days)
    )

    const upcomingReports = data.filter((project: Project) => 
      moment(getTaskStartDate(project)).isAfter(dateAfter3Days) &&
      moment(getTaskStartDate(project)).isSameOrBefore(dateAfter46Days)
    )

    return {
      reportsDueCount: dueReports.length,
      upcomingReportsCount: upcomingReports.length
    }
  }, [data]);

  const filteredData = useMemo(() => {
    const isMatchingProjectAccountOrOpportunity = (project: Project) => {
      if(!searchTerm) return true;
  
      const search = searchTerm.toLowerCase()
      const projectName = project.Name.toLowerCase();
      const accountName = project.inspire1__Account__r.Name.toLowerCase();
      const opportunityName = project.inspire1__Opportunity1__r.Related_Certificate_Number__c ?? "";
  
      return projectName.includes(search) || accountName.includes(search) || opportunityName.includes(search);
    };

    const isMatchingStatus = (project: Project) => !statusFilter || project.inspire1__Project_Status__c === statusFilter

    const isBetweenDates = (project: Project) => {
      const [fromDate, toDate] = dates ?? [];

      if(!fromDate || !toDate) return true;

      const dueDate = project.inspire1__Inspire_Tasks__r?.records?.[0]?.inspire1__Finish_Date_Only__c ?? null;
      return moment(dueDate).isBetween(fromDate, toDate)
    }
    
    return data.filter((project: Project) => 
      isMatchingProjectAccountOrOpportunity(project) &&
      isMatchingStatus(project) &&
      isBetweenDates(project)
    )
  }, [data, searchTerm, statusFilter, dates]);

  const statusOptions: string[] = useMemo(()=> [
    ...new Set(
      data
        .map((project: Project) => project.inspire1__Project_Status__c)  // Use projectStatus
        .filter(Boolean)  // Exclude null and undefined
    ),
  ], [data]);

  const isFilterActive = Boolean(searchTerm || statusFilter || dates?.length)

  const handleReportCardClick = (card: "upcomingReports" | "retportsDue") => {
    const days = card === "upcomingReports" ? "46": "3"
    const startDate = days === "3" ? moment().toDate() : moment().add("3", "days").toDate();
    const endDate = moment().add(days, "days").toDate();

    const [start, end] = dates || [];
    if(moment(start).isSame(startDate, 'date') && moment(end).isSame(endDate, 'date')) {
      setDates([]);
    } else {
      setDates([startDate, endDate])
    }
  }
  return (
    <>
      <Sidebar />
      <main className="container-fluid dashboard-bg">
        <div className="main main-app p-3 p-lg-4">
          <div className="d-flex">
          <h1 className="custom-header">Welcome, {user?.firstName}</h1>
          <div className="back-to" onClick={() => window.location.href = `${process.env.REACT_APP_LOGOUT_URL}` as string || ""}><img src={backArrowOutline} alt="Upload Icon" className="save-icon" /><div>Go Back To Home</div></div></div>
          <hr style={{ borderColor: "#919191", borderWidth: "1px" }} className="my-5" />
          <div className="row my-4">
            <div className="col-lg-3 col-md-4 col-sm-12 mb-4">
              <ReportCard
                color="#C43131"
                title="REPORTS DUE"
                count={reportsDueCount}
                nextDueDay="3 days"
                onClick={() => handleReportCardClick("retportsDue")}
              />
            </div>
            <div className="col-lg-3 col-md-4 col-sm-12 mb-4">
              <ReportCard
                color="#F49632"
                title="UPCOMING REPORTS"
                count={upcomingReportsCount}
                nextDueDay="46 days"
                onClick={() => handleReportCardClick("upcomingReports")}
              />
            </div>
          </div>
          <div className="row g-3">
            <div className="col-12">
              <div className="card card-table p-10">
                <div className="card-body px-0">

                  <Row>
                    <Col xs={6} md={6}>
                      <p style={{ fontWeight: 800, color: "#353535", fontSize: "18px", marginTop: "10px" }}>Your Audit Projects</p>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="d-flex justify-content-end align-items-center">
                        <p className="mb-0 view-as">View As</p>
                        <div className="btn-group table-view-btn-container">
                          <button
                            className="btn btn-light table-view-btn"
                            type="button"
                            id="listViewBtn"
                            aria-label="List View"
                            style={context === "list" ? { fontWeight: "bold" } : {}}
                            onClick={() => handleViewChange('list')}
                          >
                            <img src={context === "list" ? listActive : listInActive} alt="List Icon" className="table-view-btn-icon" />
                            &nbsp; List
                          </button>
                          <button
                            className="btn btn-light table-view-btn"
                            type="button"
                            id="gridViewBtn"
                            aria-label="Grid View"
                            style={context === "grid" ? { fontWeight: "bold" } : {}}
                            onClick={() => handleViewChange('grid')}
                          >
                            <img src={context === "grid" ? gridActive : gridInActive} alt="Grid Icon" className="table-view-btn-icon" />
                            &nbsp; Grid
                          </button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={2} lg={2}></Col>
                    <Col md={10}>
                      <div className="d-flex justify-content-end">
                        <div className="d-flex align-items-center dhsearch-container">
                          <input
                            type="text"
                            className="dhsearch form-control ps-3 pe-5"
                            placeholder="Search Project..."
                            aria-label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <button className="dhsearch-icon-btn"><i className="fa fa-search"></i></button>
                        </div>

                        <Dropdown className="table-status-dropdown" placeholder="Select Status" autoFocus={false} options={statusOptions} value={statusFilter} onChange={(event: any) => setStatusFilter(event.value)} />
                        <FloatLabel>
                          <Calendar
                            className="dhcalander"
                            inputId="dh_calander"
                            value={dates}
                            onChange={(e: any) => setDates(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            hideOnRangeSelection
                            showIcon
                          />
                          <label className="calendar-text" htmlFor="dh_calander">Filter by date range (due date)</label>
                        </FloatLabel>
                        {isFilterActive && <button
                          className="btn"
                          style={{ margin: " 0 0 5px 20px", backgroundColor: "rgb(196, 49, 49)", color: "#fff" }}
                          onClick={() => {
                            setSearchTerm('');
                            setDates(null);
                            setStatusFilter('');
                          }}
                        >
                          Clear
                        </button>}
                      </div>
                    </Col>
                  </Row>
                  <br />

                  <DataTable
                    initialData={filteredData}
                    columns={projectTableColumns}
                    context={context}
                    isLoadingTable={isLoadingTable}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Dashboard;
